import { SyntheticEvent } from 'react';
import { removeLineBreaks } from 'common/util/string';

const isText = (textCandidate: Node): textCandidate is Text => textCandidate.nodeType === Node.TEXT_NODE;

const isElement = (
  elementCandidate: Node,
): elementCandidate is Element => elementCandidate.nodeType === Node.ELEMENT_NODE;

const textInsideSingleCellCopied = (selectedItems: Array<ChildNode>): boolean => selectedItems.every(isText);

export const TABLE_PART_TYPE_ATTRIBUTE = 'data-table-part-type';

export enum TABLE_PARTS {
  ROW = 'row',
  CELL = 'cell',
}

const isTablePart = (
  element: Element,
  tablePartType: TABLE_PARTS,
): boolean => element.getAttribute(TABLE_PART_TYPE_ATTRIBUTE) === tablePartType;

const areElements = (
  elementCandidates: Array<Node>,
): elementCandidates is Array<Element> => elementCandidates.every(isElement);

const areTableParts = (
  elements: Array<Node>,
  tablePartType: TABLE_PARTS,
): elements is Array<Element> => {
  if (!areElements(elements)) return false;

  return elements.every((element) => isTablePart(element, tablePartType));
};

const textAcrossSeveralCellsInOneRowSelected = (
  selectedItems: Array<ChildNode>,
): selectedItems is Array<Element> => areTableParts(selectedItems, TABLE_PARTS.CELL);

const textAcrossSeveralRowsSelected = (
  selectedItems: Array<ChildNode>,
): selectedItems is Array<Element> => areTableParts(selectedItems, TABLE_PARTS.ROW);

const extractCellText = (cell: Element): string => removeLineBreaks(cell.textContent || '');

const extractCellsText = (cells: Array<Element>): string => cells.map(extractCellText).join('\t');

const extractRowText = (
  row: Element,
): string => extractCellsText(Array.from(
  row.querySelectorAll(`[${TABLE_PART_TYPE_ATTRIBUTE}="${TABLE_PARTS.CELL}"]`),
));

export const formatCopiedAsTable = ({
  event,
}: {
  event: SyntheticEvent<Element, Event>;
}): void => {
  // When handling the copy event, the selection is a source of truth:
  // https://w3c.github.io/clipboard-apis/#example-e7776e65.
  const selection = document.getSelection();
  if (!selection) return;

  // The selection.toString() method is not suitable in this case because of the following reasons:
  //  • Cells can contain explicit line breaks looking exactly like borders between <div> tags when viewed in text.
  //  • A user can select a cell without selecting text in it. In the end, this leads to the shifting of a
  //    one current-row item to the next row.
  const selectedItems = Array.from(
    selection.getRangeAt(0).cloneContents().childNodes,
  );

  // Default copying mechanics is suitable for this case.
  if (textInsideSingleCellCopied(selectedItems)) return;

  let formattedTableString = '';

  if (textAcrossSeveralCellsInOneRowSelected(selectedItems)) {
    formattedTableString = extractCellsText(selectedItems);
  }

  if (textAcrossSeveralRowsSelected(selectedItems)) {
    formattedTableString = selectedItems
      .map(extractRowText)
      .join('\n');
  }

  ((event as unknown) as ClipboardEvent).clipboardData?.setData(
    'text/plain',
    formattedTableString,
  );

  event.preventDefault();
};
